import { HttpErrorResponse } from '@angular/common/http';
import {
  ErrorHandler,
  Injectable,
  Injector,
  NgZone,
  Provider
} from '@angular/core';

import { LivErrorResponse } from '@app/core/models/liv-response-protocol.model';
import { ToastService } from '@app/core/ui/components/toast';

import { environment } from '@env/environment';

import { ErrorService } from '../services/error.service';
import { LoggerService } from '../services/logger.service';

function isLivErrorResponse(
  error: Error | LivErrorResponse
): error is LivErrorResponse {
  const livErrorResponse = error as LivErrorResponse;

  return (
    Boolean(livErrorResponse?.status) &&
    Boolean(livErrorResponse?.error?.message)
  );
}

@Injectable({ providedIn: 'root' })
export class GlobalErrorHandlerService implements ErrorHandler {
  private readonly alertDomains = this.extractDomainsFromEnvironment();

  constructor(
    private readonly zone: NgZone,
    private readonly injector: Injector,
    private readonly errorService: ErrorService,
    private readonly loggerService: LoggerService
  ) {}

  handleError(error: Error | LivErrorResponse | HttpErrorResponse): void {
    let message: string;
    let stackTrace: string;
    let isHttpErrorResponse = false;
    let errorUrl = '';

    if (error instanceof HttpErrorResponse) {
      message = this.errorService.getServerMessage(error);
      stackTrace = this.errorService.getServerStack(error);
      isHttpErrorResponse = true;
      errorUrl = error.url ?? '';
    } else if (isLivErrorResponse(error)) {
      message = this.errorService.getServerMessage(error);
      stackTrace = this.errorService.getServerStack(error);
      isHttpErrorResponse = true;
      errorUrl = error.error?.stack?.url ?? '';
    } else {
      message = this.errorService.getClientMessage(error);
      stackTrace = this.errorService.getClientStack(error);
    }

    this.zone.run(() => {
      this.loggerService.logError(message, stackTrace);

      if (!isHttpErrorResponse) {
        return;
      }

      if (!this.shouldShowAlert(errorUrl)) {
        console.warn(
          `Error from ${errorUrl || 'unknown url'} does not match configured alert domains. No alert will be shown.`
        );
        return;
      }

      // Resolve toast lazily to avoid DI cycles during app bootstrap.
      const toast = this.injector.get(ToastService);
      toast.danger(message);
      this.loggerService.sendErrorToServer(message, stackTrace);
    });
  }

  private shouldShowAlert(url: string): boolean {
    if (!url) {
      // LivErrorResponse sem URL ainda é erro da API tratada pelo interceptor
      return true;
    }

    try {
      const domain = new URL(url).hostname;
      return this.alertDomains.some(
        (alertDomain) => domain.endsWith(alertDomain) || domain === alertDomain
      );
    } catch {
      return false;
    }
  }

  private extractDomainsFromEnvironment(): string[] {
    const domains = new Set<string>();

    if (environment.apiUrl) {
      domains.add(this.extractDomain(environment.apiUrl));
    }

    return Array.from(domains);
  }

  private extractDomain(url: string): string {
    try {
      return url.replace(/(https?:\/\/)?([^/]+).*/i, '$2');
    } catch {
      return url;
    }
  }
}

export function provideGlobalErrorHandler(): Provider {
  return {
    provide: ErrorHandler,
    useExisting: GlobalErrorHandlerService
  };
}
